import { AppStoreImageValidationModal } from "@/components/AppStoreImageValidationModal";
import { FollowConfirmationModal } from "@/components/FollowConfirmationModal";
import { ClaimModal } from "@/components/QuestModals/ClaimModal";
import { SocialEngagementModal } from "@/components/QuestModals/SocialEngagementModal";
import { TelegramQuestModal } from "@/components/QuestModals/TelegramQuestModal";
import { VideoQuestModal } from "@/components/QuestModals/VideoQuestModal";
import {
  type CampaignQuest,
  type CampaignQuestState,
  mapCampaignQuestType,
} from "@/components/QuestsV2/types";
import { LikeRetweetXModal } from "@/components/v2/LikeRetweetXModal";
import { PostTweetXModal } from "@/components/v2/PostTweetXModal";
import { CLAIM_MODAL_PARAMS } from "@/constants";
import { Action } from "@/context/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { useAccessToken } from "@/hooks/useAccessToken";
import { validateQuest } from "@/utils/linkedaccounts/linkedAccounts";
import type { SocialQuestData } from "@/utils/linkedaccounts/types";
import { Box } from "@biom3/react";
import { useCallback, useEffect, useState } from "react";
import type { GameData } from "./types";
import useGameDetailsAnalytics from "./useGameDetailsAnalytics";

export type CampaignQuestsProps = {
  game: GameData;
  completeQuest: (quest: CampaignQuest) => Promise<void>;
  inProgressQuest?: CampaignQuestState;
  setInProgressQuest: (quest: CampaignQuestState | undefined) => void;
};

export const QuestInteractionHandler = ({
  game,
  completeQuest,
  inProgressQuest,
  setInProgressQuest,
}: CampaignQuestsProps) => {
  const getAccessToken = useAccessToken();
  const { trackQuestEvent } = useGameDetailsAnalytics();
  const { isLoggedIn } = usePassportProvider();

  // Modal states
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [persistedQuestData, setPersistedQuestData] =
    useState<SocialQuestData | null>(null);
  const showXActionConfirmationModal =
    inProgressQuest?.type === "retweet_and_like";
  const showAppStoreImageValidationModal =
    inProgressQuest?.type === "app_store_image_validation";
  const showPostTweetModal = inProgressQuest?.type === "post_x_tweet";
  const showSteamWishlistModal = inProgressQuest?.type === "steam_wishlist";
  const [showReferralInvite, setShowReferralInvite] = useState(false);

  const questData: SocialQuestData = {
    ruleId: inProgressQuest?.externalId ?? "",
    key: inProgressQuest ? mapCampaignQuestType(inProgressQuest.type) : "bonus",
    questLink: inProgressQuest?.linkUrl ?? "",
    gameName: game.details.name || "",
    gameId: game.details.game_id || "",
  };

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key === "social_auth_error" &&
        event.newValue &&
        showConfirmation
      ) {
        setError(event.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [showConfirmation]);

  useEffect(() => {
    if (
      inProgressQuest?.type === "follow_x_account" ||
      inProgressQuest?.type === "join_discord_server" ||
      inProgressQuest?.type === "steam_wishlist"
    ) {
      setShowConfirmation(true);
    }
  }, [inProgressQuest]);

  const onConfirmFollow = useCallback(async () => {
    try {
      // If they failed to complete the quest make them complete the flow again so they can't spam the button and reach the rate limit
      if (error) {
        setShowConfirmation(false);
        setError(null);
        return;
      }

      const quest = inProgressQuest;
      if (!quest) {
        setShowConfirmation(false);
        setError(null);
        return;
      }

      // If the quest was completed in the login screen show the "success" state
      if (localStorage.getItem(quest.externalId) === "true") {
        setShowConfirmation(false);
        completeQuest(quest);
        setInProgressQuest(undefined);
        setError(null);
        return;
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        return;
      }

      const mappedKey = mapCampaignQuestType(quest.type);
      const following = await validateQuest(
        game.details.game_id,
        mappedKey,
        quest.externalId,
        accessToken,
        "",
      );
      if (!following) {
        throw new Error("the quest is not complete");
      }
      trackQuestEvent(Action.SUCCEEDED, "FollowConfirmation", quest);

      setShowConfirmation(false);
      setError(null);
      completeQuest(quest);
      setInProgressQuest(undefined);
    } catch (err) {
      if (inProgressQuest) {
        trackQuestEvent(Action.FAILED, "FollowConfirmation", inProgressQuest);
      }

      const errorMessage =
        err instanceof Error
          ? err.message
          : "An error occurred while confirming follow.";

      setError(errorMessage);
    }
  }, [
    inProgressQuest,
    error,
    game,
    completeQuest,
    getAccessToken,
    trackQuestEvent,
    setInProgressQuest,
  ]);

  const claimModalTitle =
    inProgressQuest?.externalId &&
    CLAIM_MODAL_PARAMS[inProgressQuest?.externalId]
      ? CLAIM_MODAL_PARAMS[inProgressQuest?.externalId].title
      : (inProgressQuest?.name ?? "");
  const claimModalCaption =
    inProgressQuest?.externalId &&
    CLAIM_MODAL_PARAMS[inProgressQuest?.externalId]
      ? CLAIM_MODAL_PARAMS[inProgressQuest?.externalId].caption
      : undefined;

  return (
    <Box>
      {/* <ReferralInviteModal
        visible={showReferralInvite}
        onClose={() => {
          setShowReferralInvite(false);
          setInProgressQuest(undefined);
        }}
        gameUid={game.uid}
        campaignId={campaignId}
        campaignPoints={inProgressQuest?.campaignPoints}
      /> */}
      <FollowConfirmationModal
        visible={showConfirmation}
        onConfirm={onConfirmFollow}
        onClose={() => {
          setShowConfirmation(false);
          setError(null);
          setInProgressQuest(undefined);
          setPersistedQuestData(null);
          localStorage.removeItem("social_auth_error");
        }}
        onRetry={() => {
          if (inProgressQuest) {
            setError(null);
            localStorage.removeItem("social_auth_error");
            window.open(inProgressQuest.linkUrl || "", "_blank");
          }
        }}
        onQuestClick={() => {}}
        onSkip={() => {}}
        questKey={
          persistedQuestData?.key ||
          (inProgressQuest
            ? mapCampaignQuestType(inProgressQuest.type)
            : undefined)
        }
        linkUrl={persistedQuestData?.questLink || inProgressQuest?.linkUrl}
        ruleId={persistedQuestData?.ruleId || inProgressQuest?.externalId}
        gameName={game.details.name}
        error={error}
        setError={setError}
      />
      {inProgressQuest?.type === "post_x_tweet" &&
        inProgressQuest?.primitiveData?.type === "x" && (
          <PostTweetXModal
            visible={showPostTweetModal}
            onClose={() => {
              setInProgressQuest(undefined);
            }}
            showConfirmationModal={() => {
              setPersistedQuestData(questData);
              setShowConfirmation(true);
            }}
            questDetails={{
              questRuleId: questData.ruleId,
              questKey: questData.key,
              questLink: questData.questLink,
            }}
            gameId={questData.gameId}
            gameName={questData.gameName}
            gameUid={game.details.game_id}
            tweetDetails={{
              tweetText: inProgressQuest.primitiveData.tweetText || "",
              tweetUsername:
                inProgressQuest.primitiveData.tweetUsername ||
                inProgressQuest.primitiveData.tweetSubstring ||
                "",
              tweetRequiresImage:
                inProgressQuest.primitiveData.tweetRequiresImage || false,
            }}
            completionDetails={{
              completed: inProgressQuest.completedToday,
              latestCompletion: inProgressQuest.latestCompletion,
              refreshPeriodHours:
                inProgressQuest.refreshPeriodHours ?? undefined,
              validFrom: inProgressQuest.validFrom ?? undefined,
            }}
          />
        )}
      {inProgressQuest?.type === "retweet_and_like" &&
        inProgressQuest?.externalId && (
          <LikeRetweetXModal
            visible={showXActionConfirmationModal}
            onClose={() => {
              setInProgressQuest(undefined);
            }}
            questRuleId={questData.ruleId}
            questKey={questData.key}
            questLink={questData.questLink}
            gameId={questData.gameId}
            gameName={questData.gameName}
            showConfirmationModal={() => {
              setPersistedQuestData(questData);
              setShowConfirmation(true);
            }}
            tweetId={
              inProgressQuest.primitiveData?.type === "x"
                ? inProgressQuest.primitiveData.likeRetweetId || ""
                : ""
            }
          />
        )}
      {inProgressQuest?.type === "join_telegram_chat" && (
        <TelegramQuestModal
          gameId={game.details.game_id}
          gameName={game.details.name}
          questKey={"telegram-v2"}
          ruleId={inProgressQuest.externalId}
          onClose={() => {
            setInProgressQuest(undefined);
          }}
          onQuestComplete={() => {
            completeQuest(inProgressQuest);
          }}
        />
      )}
      {inProgressQuest?.type === "social_engagement" && (
        <SocialEngagementModal
          gameId={game.details.game_id}
          gameName={game.details.name}
          visible={true}
          onClose={() => {
            setInProgressQuest(undefined);
          }}
          rewardType={"points"}
          hasMultiplier={inProgressQuest.categories.includes(
            "engagement_multiplier",
          )}
          questId={inProgressQuest.externalId}
        />
      )}
      {inProgressQuest?.type === "claim" && (
        <ClaimModal
          title={claimModalTitle}
          caption={claimModalCaption}
          onViewQuest={() => {
            window.open(inProgressQuest.linkUrl || "", "_blank");
          }}
          onClaim={async () => {
            await completeQuest(inProgressQuest);
            setInProgressQuest(undefined);
          }}
          onClose={() => {
            setInProgressQuest(undefined);
          }}
        />
      )}
      {inProgressQuest?.type === "app_store_image_validation" && (
        <AppStoreImageValidationModal
          visible={showAppStoreImageValidationModal}
          onClose={() => {
            setInProgressQuest(undefined);
          }}
          validationType={inProgressQuest?.validationType}
          questId={inProgressQuest?.externalId}
          gameUid={game.details.slug || ""}
          gameId={game.details.game_id || ""}
        />
      )}
      {inProgressQuest?.type === "trailer" && (
        <VideoQuestModal
          videoUrl={inProgressQuest.linkUrl || ""}
          videoMimeType={"iframe/embed"}
          onClose={() => {
            setInProgressQuest(undefined);
          }}
          onComplete={async () => {
            await completeQuest(inProgressQuest);
            setInProgressQuest(undefined);
          }}
          showControls={false}
        />
      )}
    </Box>
  );
};
